import { Subject } from 'rxjs'
import { auditTime } from 'rxjs/operators'
import { computed, DestroyRef, effect, Injectable, Injector, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { TranslateService } from '@ngx-translate/core'
import Flow from '@flowjs/flow.js'
import type { FlowFile } from 'flowjs'

import { MediaRootDetail, VolumeTypeEnum } from 'elements-sdk'
import { APIService } from 'api'
import { MessageBoxService } from 'common/util'
import { NotifyService } from 'common/util/services/notify.service'
import { PageTitleService } from 'common/util/services/pageTitle.service'
import { CryptoRandomService } from 'common/util/services/cryptoRandom.service'
import { BeforeUnloadService } from 'main/services/beforeUnload.service'
import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { ROUTE_DETACH_REF } from 'root/routeReuseStrategy'
import { TasksService } from 'tasks/services/tasks.service'

export enum UploadState {
  IDLE = 0,
  PENDING = 1,
  UPLOADING = 2,
  PAUSED = 3,
  DONE = 4,
}

interface FlowFileExtras {
  fullPath: string
  path?: string // original upload destination path
  config?: UploaderConfig // config at the time of upload
  pending?: boolean
  finishing?: boolean
  done?: boolean
}

export type File = FlowFile & FlowFileExtras

export abstract class GenericFileReader {
  name: string

  constructor (private relativePath: string, private size: number) {
    this.name = relativePath.split('/').pop() ?? ''
  }

  abstract read (start: number, length: number): Promise<Blob>
}

function readFileFn (fileObj, startByte, endByte, fileType, chunk) {
  if (fileObj.file instanceof GenericFileReader) {
    fileObj.file.read(startByte, endByte - startByte).then(data => {
      chunk.readFinished(data)
    })
    return
  }

  let function_name = 'slice'

  if (fileObj.file.slice) {
    function_name = 'slice'
  } else if (fileObj.file.mozSlice) {
    function_name = 'mozSlice'
  } else if (fileObj.file.webkitSlice) {
    function_name = 'webkitSlice'
  }

  chunk.readFinished(fileObj.file[function_name](startByte, endByte, fileType))
}

export interface UploaderConfig {
  path: string
  mediaRoot: MediaRootDetail
}

@Injectable({ providedIn: 'root' })
export class UploaderService {
  readonly state = UploadState

  readonly uploaded$ = new Subject<File>()
  readonly filesAdded$ = new Subject<File[]>()
  readonly fileRemoved$ = new Subject<File>()
  readonly complete$ = new Subject<void>()
  readonly cancelled$ = new Subject<void>()

  flow: Flow

  private _currentConfig = signal<UploaderConfig | null>(null)
  readonly currentConfig = this._currentConfig.asReadonly()
  readonly active = computed(() => this._currentConfig() !== null)
  readonly permission = computed(() => {
    const cfg = this._currentConfig()
    if (!cfg) {
      return null
    }
    return this.mediaPermissions.getPermissionForPath(cfg.path)
  })

  showDragHint = false

  constructor (
    private api: APIService,
    private beforeUnload: BeforeUnloadService,
    private translate: TranslateService,
    private pageTitle: PageTitleService,
    private mediaPermissions: MediaPermissionsService,
    private messageBox: MessageBoxService,
    private notify: NotifyService,
    private tasks: TasksService,
    private cryptoRandom: CryptoRandomService,
  ) {
    this.flow = new Flow({
      allowDuplicateUploads: true,
      chunkSize: 1024 * 1024 * 10,
      simultaneousUploads: 5,
      method: 'octet',
      target: '/api/2/uploads/chunk',
      generateUniqueIdentifier: (file: File) => {
        // Some confusion in different versions of Firefox
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Unclear if '' || '' is the intent.
        const relativePath = file.relativePath || file['webkitRelativePath'] || file['fileName'] || file.name
        const ts = Math.floor(Date.now())
        const rnd = this.cryptoRandom.hex(8)
        return `${file.size}-${relativePath.replace(/[^\w-]/gim, '')}-${ts}-${rnd}`
      },
      readFileFn,
    })
    this.flow.on('fileProgress', this.onFileProgress.bind(this))
    this.flow.on('complete', this.onComplete.bind(this))
    this.flow.on('fileSuccess', this.onFileUploaded.bind(this))
    this.flow.on('filesSubmitted', this.onFilesAdded.bind(this))
    this.flow.on('fileRemoved', this.onFileRemoved.bind(this))
    this.flow.on('pause' as any, this.updatePageTitle.bind(this)) // A valid hook; it's missing from types. Source: https://github.com/flowjs/flow.js/blob/3d4ba3a1b9906b31551f33ae649e6fd6b95cf711/src/flow.js#L173

    const dragState = new Subject<boolean>()
    dragState.pipe(auditTime(100)).subscribe(v => {
      this.showDragHint = v
    })
    document.body.addEventListener('dragover', () => {
      dragState.next(this.active())
    })
    document.body.addEventListener('dragleave', () => {
      dragState.next(false)
    })
    document.body.addEventListener('drop', () => {
      dragState.next(false)
    })

    effect(() => {
      if (this.active()) {
        this.flow.assignDrop(document.body)
      } else {
        this.flow.unAssignDrop(document.body)
      }
    })
  }

  getState (): UploadState {
    if (!this.flow.files.length) {
      return UploadState.IDLE
    }

    if (this.flow.files.some((x: File) => x.isUploading())) {
      return UploadState.UPLOADING
    }

    if (this.flow.files.some((x: File) => x.paused)) {
      return UploadState.PAUSED
    }

    return UploadState.DONE
  }

  getProgress (): number {
    if (![UploadState.UPLOADING, UploadState.PAUSED].includes(this.getState())) {
      return 1.0
    }
    let uploaded = 0
    let total = 0
    for (const file of this.flow.files) {
      total += file.size
      uploaded += file.sizeUploaded()
    }
    return uploaded / (total || 1)
  }

  getUploadSpeed (): number {
    return this.flow.files.map((f: File) => f.currentSpeed).reduce((s: number, n: number) => s + n, 0)
  }

  getPageTitle (): string | null {
    const state = this.getState()
    if (![UploadState.UPLOADING, UploadState.PAUSED].includes(state)) {
      return null
    }

    return `[${Math.floor(this.getProgress() * 100)}%${state === UploadState.PAUSED ? ' PAUSED' : ''}] `
  }

  activateForView (viewInjector: Injector, config: UploaderConfig): void {
    this.deactivate()
    this._currentConfig.set(config)
    const detachRef = viewInjector.get(ROUTE_DETACH_REF, null)
    const destroyRef = viewInjector.get(DestroyRef)
    destroyRef.onDestroy(() => {
      this.deactivate()
    })
    if (detachRef) {
      detachRef.viewIsAttached$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(attached => {
          if (attached) {
            this.activate(config)
          } else {
            this.deactivate()
          }
        })
    }
  }

  activate (config: UploaderConfig): void {
    this.deactivate()
    this._currentConfig.set(config)
  }

  deactivate (): void {
    this._currentConfig.set(null)
  }

  clear (): void {
    const hadFiles = this.flow.files.length > 0

    for (const file of [...this.flow.files]) {
      try {
        file.cancel()
      } catch {
        // Nothing.
      }
    }

    this.flow.files.length = 0

    if (hadFiles) {
      this.cancelled$.next()
    }
  }

  async onFilesAdded (files: File[]): Promise<void> {
    const config = this.currentConfig()
    if (!config) {
      return
    }

    const batch = files.map(file => {
      file.config = config
      file.path = config.path
      file.pending = true
      file.pause()
      file.fullPath = file.path.replace(/\/$/, '') + '/' + file.relativePath
      return {
        uploadId: file.uniqueIdentifier,
        path: file.fullPath,
      }
    })

    const response = await this.api.main.registerBatchUpload({
      registerUpload: batch,
    })

    const registeredIds = response.filter(file => file.success).map(file => file.uploadId)
    const results = files.filter(file => registeredIds.includes(file.uniqueIdentifier))

    this.filesAdded$.next(results)
    this.updatePageTitle()
  }

  async onFileUploaded (file: File): Promise<void> {
    file.finishing = true
    try {
      const task = await this.api.main.finishUpload({
        finishUploadEndpointRequest: {
          uploadId: file.uniqueIdentifier,
        },
      })
      await this.tasks.waitTasksComplete([task.id])
      file.fullPath = task.kwargs.full_path ?? file.fullPath
      file.name = task.kwargs.file_name ?? file.name
      file.relativePath = file.name
      file.done = true
      this.notify.info(this.translate.instant('Upload complete'))
    } catch (error) {
      file.error = true
      this.notify.errorFromResponse(this.translate.instant('Upload failed'), error)
      return
    } finally {
      file.finishing = false
      this.updatePageTitle()
    }

    this.uploaded$.next(file)

    const volume = file.config?.mediaRoot.volume
    const canUnzip = volume != null && volume.type !== VolumeTypeEnum.Cloud // Unzipping can't succeed on cloud volumes.
    if (canUnzip && /\.zip$/i.test(file.name)) {
      // Wait for possible route changes that would close the modal
      setTimeout(async () => {
        try {
          await this.messageBox.show({
            title: 'ZIP',
            text: this.translate.instant('Unpack this zip file?'),
            positive: this.translate.instant('Unpack'),
            negative: this.translate.instant('Don\'t unpack'),
          })
        } catch {
          return
        }
        this.api.storage.unzipFile({
          fileUnzipEndpointRequest: {
            input: `${file.path}/${file.name}`,
            remove: true,
          },
        })
      }, 500)
    }
  }

  onFileProgress (): void {
    this.updatePageTitle()
    if (this.getState() === UploadState.UPLOADING) {
      this.beforeUnload.preventUnload(this.translate.instant('Uploads are running. Leave?'))
    }
  }

  onFileRemoved (file: File): void {
    this.updatePageTitle()
    this.fileRemoved$.next(file)
  }

  async onComplete (): Promise<void> {
    this.updatePageTitle()
    this.beforeUnload.allowUnload()
    this.complete$.next()
  }

  private updatePageTitle (): void {
    this.pageTitle.setPrefix(this.getPageTitle())
  }
}
